import React, { useState, useEffect, useRef, useMemo } from "react";
import { StyledImgProps } from '../dto/StyleCompRepository';
import { DisplayAlignment, DisplaySize } from '../enum/EnumRepository';
import { ImageMetadata } from '../dto/EntityRepository';
import { isBase64, getFallbackDeviceType, convertToJsonMetadata, isDeviceAvailable, generateOptimizedUrl } from '../util/findAdjustImg';
import defaultImg from '../../asset/project/default_1.png'
import { debounce } from '../util/debounce';
import { useOnScreen } from '../hooks/useOnScreen';

interface OptimizedImgProps {
  imageUrl: string;
  alt: string;
  StyledImg: React.ComponentType<StyledImgProps>;
  displaySize: DisplaySize;
  displayAlignment: DisplayAlignment;
  priority: boolean;
  handleClick?: () => void;
  imageRatio?: number;
}

/**
 * 이미지 최적화 컴포넌트
 * 
 * 1. 화면 가시성 감지 (Lazy Loading) - IntersectionObserver로 화면 진입 전까지 로딩 지연
 * 2. 메타데이터 로드 - meta.json에서 사용 가능한 이미지 변형 정보 확인
 * 3. 디바이스 타입 결정 - ResizeObserver로 컨테이너 크기에 맞는 디바이스 타입 선택
 * 4. 최적화된 URL 생성 - 메타데이터와 디바이스 타입 기반으로 최적화된 이미지 URL 생성
 */
const OptimizedImg: React.FC<OptimizedImgProps> = ({ imageUrl, alt, StyledImg, displaySize, displayAlignment, priority = false, handleClick, imageRatio }) => {
  const [metadata, setMetadata] = useState<ImageMetadata | null>(null);
  const [metadataFetched, setMetadataFetched] = useState(false);
  const [browserType, setBrowserType] = useState<string | null>(null);
  const [adjustedUrl, setAdjustedUrl] = useState<string | null>(null);
  const currentDeviceTypeRef = useRef<string | null>(null);
  const previousAdjustedUrlRef = useRef<string | null>(null);

  // 1단계: 화면 가시성 감지 (priority: 1200px, 일반: 600px)
  const { ref: imgRef, visible } = useOnScreen<HTMLImageElement>(priority ? "1200px" : "600px");
  const shouldLoad = priority || visible;
  
  const isHttpUrl = useMemo(() => imageUrl && imageUrl.startsWith('http'), [imageUrl]);
  const isBase64Img = useMemo(() => isBase64(imageUrl), [imageUrl]);

  // 2단계: 메타데이터 로드 (meta.json fetch)
  // 이미지 URL과 같은 디렉토리의 meta.json에서 사용 가능한 이미지 변형 정보 확인
  useEffect(() => {
    const fetchMetadata = async () => {
      try {
        const jsonUrl = imageUrl.replace(/\/[^/]+\.(?:jpeg|jpg|png|gif|webp)$/, `/meta.json`);
        const response = await fetch(jsonUrl, { cache: 'no-cache' });
        if (!response.ok) {
          throw new Error('Failed to fetch metadata');
        }
        const contentType = response.headers.get("content-type");
        if (contentType && contentType.includes("application/json")) {
          const data = await response.json();
          const imageMetadata = convertToJsonMetadata(data);

          setMetadata(imageMetadata);
          setMetadataFetched(true);
          
          // 메타데이터 로드 후 초기 디바이스 타입 설정
          if (imgRef.current) {
            const containerWidth = imgRef.current.offsetWidth || imgRef.current.getBoundingClientRect().width;
            const deviceType = getFallbackDeviceType(imageMetadata, containerWidth);
            if (currentDeviceTypeRef.current !== deviceType) {
              setBrowserType(deviceType);
              currentDeviceTypeRef.current = deviceType;
            }
          }
        } else {
          throw new Error('Response is not JSON');
        }
      } catch (error) {
        // meta.json이 없거나 실패한 경우 원본 이미지 사용
        setMetadata(null);
        setMetadataFetched(true);
      }
    };

    if (imageUrl && !isBase64Img && isHttpUrl) {
      setMetadataFetched(false);
      fetchMetadata();
    } else {
      setMetadataFetched(true);
    }
  }, [imageUrl, isBase64Img, isHttpUrl]);

  // 3단계: 컨테이너 크기 변화 감지 및 디바이스 타입 업데이트
  // ResizeObserver로 컨테이너 크기 변화 감지 → 디바운스(200ms) 적용 → 적절한 디바이스 타입 선택
  useEffect(() => {
    if (!imgRef.current || !metadata || isBase64Img || !isHttpUrl) return;

    // 디바운스된 콜백 함수 정의 (200ms 지연)
    const debouncedCallback = debounce((entry) => {
      const width = entry.contentRect.width;
      const newDeviceType = getFallbackDeviceType(metadata, width);
      if (currentDeviceTypeRef.current !== newDeviceType) {
        setBrowserType(newDeviceType);
        currentDeviceTypeRef.current = newDeviceType;
      }
    }, 200);

    const ro = new ResizeObserver(([entry]) => {
      debouncedCallback(entry);
    });

    ro.observe(imgRef.current);
    
    // 초기 디바이스 타입 설정
    const initialWidth = imgRef.current.offsetWidth || imgRef.current.getBoundingClientRect().width;
    const initialDeviceType = getFallbackDeviceType(metadata, initialWidth);
    if (currentDeviceTypeRef.current !== initialDeviceType) {
      setBrowserType(initialDeviceType);
      currentDeviceTypeRef.current = initialDeviceType;
    }
    
    return () => {
      ro.disconnect();
      debouncedCallback.cancel();
    };
  }, [metadata, imageUrl, isBase64Img, isHttpUrl]);

  // 4단계: 최적화된 이미지 URL 결정
  // Base64/비HTTP → 원본 | 미가시화/메타미완료 → null | 메타없음 → 원본 | 메타+디바이스타입 → 최적화URL | 그 외 → 원본
  useEffect(() => {
    let newUrl: string | null = null;
    
    if (isBase64Img || !isHttpUrl) { 
      newUrl = imageUrl;
    } else if (!shouldLoad) {
      newUrl = null;
    } else if (!metadataFetched) {
      newUrl = null;
    } else if (!metadata) {
      newUrl = imageUrl;
    } else if (imageUrl && metadata && browserType && isDeviceAvailable(metadata, browserType)) {
      newUrl = generateOptimizedUrl(imageUrl, browserType, metadata);
    } else {
      newUrl = imageUrl;
    }
    
    // 이전 URL과 동일하면 업데이트 생략 (불필요한 리렌더링 방지)
    if (previousAdjustedUrlRef.current !== newUrl) {
      setAdjustedUrl(newUrl);
      previousAdjustedUrlRef.current = newUrl;
    }
  }, [browserType, imageUrl, metadata, metadataFetched, shouldLoad, isBase64Img, isHttpUrl]);

  // 최종 src 결정: imageUrl이 빈 문자열이거나 adjustedUrl이 null이면 defaultImg, 아니면 adjustedUrl
  const finalSrc = useMemo(() => {
    return imageUrl === '' ? defaultImg : (adjustedUrl || defaultImg);
  }, [imageUrl, adjustedUrl]);

  return (
    <StyledImg
      ref={imgRef}
      src={finalSrc}
      alt={alt}
      $displaySize={displaySize}
      $displayAlignment={displayAlignment}
      $imageRatio={imageRatio}
      loading={priority ? "eager" : "lazy"}
      decoding="async"
      onClick={handleClick}
    />
  );
};

export default OptimizedImg;
